import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, Car, ArrowRightCircle, TrendingUp, Wallet, Sparkles,
  Clock, Crown, AlertCircle, DollarSign, Loader2,
} from "lucide-react";
import { api } from "../../../lib/api";

/**
 * CRMDashboard — one screen every morning: today's rides, cars out on the
 * road, deposits pending, follow-ups due, MTD revenue. Pulls everything
 * from GET /admin/crm/dashboard which aggregates across bookings, deposits,
 * crm_leads and crm_profiles.
 */
export default function CRMDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/crm/dashboard")
      .then((r) => setStats(r.data))
      .catch(() => toast.error("Failed to load CRM dashboard"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-10 text-white/50 text-sm inline-flex items-center gap-2">
        <Loader2 size={14} className="animate-spin text-cyan" /> Loading dashboard…
      </div>
    );
  }

  const cards = [
    { key: "todays_bookings",    label: "Today's Bookings",   icon: Calendar,        tone: "cyan"   },
    { key: "vehicles_out",       label: "Vehicles Out",       icon: Car,             tone: "amber"  },
    { key: "returning_today",    label: "Returning Today",    icon: ArrowRightCircle, tone: "lime"  },
    { key: "upcoming",           label: "Upcoming · 7d",      icon: TrendingUp,      tone: "cyan"   },
    { key: "deposits_pending",   label: "Deposits Pending",   icon: Wallet,          tone: "amber"  },
    { key: "outstanding_balances", label: "Outstanding",      icon: DollarSign,      tone: "red"    },
    { key: "new_leads",          label: "New Leads",          icon: Sparkles,        tone: "cyan"   },
    { key: "follow_ups_due",     label: "Follow-ups Due",     icon: Clock,           tone: "amber"  },
    { key: "vip_customers",      label: "VIP Customers",      icon: Crown,           tone: "gold"   },
    { key: "revenue_mtd",        label: "Revenue · MTD",      icon: DollarSign,      tone: "lime", money: true },
  ];

  return (
    <div data-testid="crm-dashboard" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map((c) => {
        const v = stats?.[c.key] || 0;
        return (
          <Card key={c.key} label={c.label} icon={c.icon} tone={c.tone}
                value={c.money ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : v} />
        );
      })}
    </div>
  );
}

function Card({ label, value, icon: Icon, tone = "cyan" }) {
  const tones = {
    cyan:  "text-cyan border-cyan/25 bg-cyan/[0.04]",
    amber: "text-amber-300 border-amber-400/25 bg-amber-500/[0.04]",
    lime:  "text-lime-300 border-lime-400/25 bg-lime-500/[0.04]",
    red:   "text-red-300 border-red-400/25 bg-red-500/[0.04]",
    gold:  "text-yellow-200 border-yellow-400/25 bg-yellow-500/[0.04]",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <div className="label-caps text-[9px] text-white/60 tracking-widest">{label}</div>
        <Icon size={13} className="opacity-70" />
      </div>
      <div className="font-serif text-3xl mt-2">{value}</div>
    </div>
  );
}
